import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { ReplaySubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { StmToastService } from '../../shared/ui/toast/toast.service';

export type DeploymentMetadata = {
    release: string;
    commitSha?: string;
};

type VersionMetadata = {
    hash?: string;
    releaseTag?: string;
    commitSha?: string;
};

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
    private readonly destroyRef = inject(DestroyRef);
    private _translate = inject(TranslocoService);
    private http = inject(HttpClient);
    private toastrService = inject(StmToastService);

    // this will be replaced by the actual hash in post-build.js
    private readonly bundleHash = '{{POST_BUILD_ENTERS_HASH_HERE}}';
    private currentHash = this.bundleHash;
    private readonly deploymentMetadataSubject = new ReplaySubject<DeploymentMetadata>(1);
    private hasPublishedDeploymentMetadata = false;
    private versionCheckIntervalId: number | undefined;
    private reloadTimeoutId: number | undefined;

    readonly deploymentMetadata$ = this.deploymentMetadataSubject.asObservable();

    constructor() {
        this.destroyRef.onDestroy(() => {
            if (this.versionCheckIntervalId !== undefined) {
                window.clearInterval(this.versionCheckIntervalId);
            }
            if (this.reloadTimeoutId !== undefined) {
                window.clearTimeout(this.reloadTimeoutId);
            }
        });
    }

    /**
     * Checks the frontend application version at a fixed interval.
     * @param url
     * @param frequency in milliseconds, defaults to 5 minutes
     */
    public initVersionCheck(url: string, frequency = 1000 * 60 * 5): void {
        this.checkVersion(url);

        if (this.versionCheckIntervalId !== undefined) {
            window.clearInterval(this.versionCheckIntervalId);
        }

        this.versionCheckIntervalId = window.setInterval(() => {
            this.checkVersion(url);
        }, frequency);
    }

    /**
     * Calls the version endpoint and checks whether the hash changed.
     * @param url
     */
    private checkVersion(url: string): void {
        // timestamp these requests to invalidate caches
        this.http
            .get<VersionMetadata>(url + '?t=' + Date.now())
            .pipe(first(), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    const hash = response.hash ?? '';
                    const hashChanged = this.hasHashChanged(this.currentHash, hash);

                    if (!this.hasPublishedDeploymentMetadata && this.matchesRunningBundle(hash)) {
                        this.publishDeploymentMetadata(response);
                    }

                    // If new version, do something
                    if (hashChanged) {
                        this.toastrService.info(this._translate.translate<string>('Toast.NewVersion.Message'), this._translate.translate<string>('Toast.NewVersion.Title'), { durationMs: null });

                        this.reloadTimeoutId = window.setTimeout(() => {
                            location.reload();
                        }, 10000);
                    }
                    // store the new hash so we wouldn't trigger versionChange again
                    // only necessary in case you did not force refresh
                    this.currentHash = hash;
                },
                error: (err) => {
                    console.error(err, 'Could not get version');
                }
            });
    }

    private publishDeploymentMetadata(metadata: VersionMetadata): void {
        const release = metadata.releaseTag?.trim() || metadata.commitSha?.trim() || metadata.hash?.trim();
        if (!release) {
            return;
        }

        this.hasPublishedDeploymentMetadata = true;
        this.deploymentMetadataSubject.next({
            release,
            commitSha: metadata.commitSha?.trim() || undefined
        });
    }

    private matchesRunningBundle(hash: string): boolean {
        return !this.bundleHash || this.bundleHash === '{{POST_BUILD_ENTERS_HASH_HERE}}' || this.bundleHash === hash;
    }

    /**
     * Checks if hash has changed.
     * If the runtime hash differs from `version.json`, the app version changed.
     * @param currentHash
     * @param newHash
     * @returns
     */
    private hasHashChanged(currentHash: string, newHash: string) {
        if (!currentHash || currentHash === '{{POST_BUILD_ENTERS_HASH_HERE}}') {
            return false;
        }

        return currentHash !== newHash;
    }
}
